import type { IconProps } from '@design/components/icon/Icon';
import type { Row } from '@tanstack/react-table';
import type { Property } from 'csstype';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { type AppTableFeatures } from '../react-table-features';

export type RevampTableColumnType =
  | 'text'
  | 'directional'
  | 'number'
  | 'status'
  | 'entity';

export const revampTableKnownStatusTypes = ['Success', 'Failed'] as const;

export type RevampTableKnownStatus =
  (typeof revampTableKnownStatusTypes)[number];

export type RevampTableStatusType<T extends string = string> =
  | RevampTableKnownStatus
  | T;

export type RevampTableData = {
  value: string | number;
  tooltip?: string;
  logo?: string | null;
};

export type RevampTableRow<T extends string = string> = Record<
  T,
  RevampTableData
>;

export type RevampTableColumn = {
  id: string;
  title: string;
  type: RevampTableColumnType;
  alignment?: Property.TextAlign;
  isNumber?: boolean;
  tooltip?: string;
  sticky?: boolean | null;
};

export type RevampTablePayload<T extends string = string> = {
  columnsMeta: RevampTableColumn[];
  data: RevampTableRow<T>[];
  stickyHeader?: boolean | null;
};

export type PickedRevampTablePayload = Pick<
  RevampTablePayload,
  'columnsMeta' | 'data' | 'stickyHeader'
>;

export type RevampTableProps = PickedRevampTablePayload & {
  /** Extra class forwarded to the outer wrapper. */
  className?: string;
  /** Optional content rendered above the table (e.g. title, filters). */
  customHeader?: ReactNode;
  /** Element rendered when `sliceIndex` truncates the data (e.g. a "Show all" button). */
  floatingExpandBtn?: ReactNode;
  /** Show the rounded border around the table wrapper. Defaults to `true`. */
  showBorder?: boolean;
  /** When set, only the first N rows are rendered. Combine with `floatingExpandBtn` for "show first N then expand". */
  sliceIndex?: number;
  /** When true, caps the visible rows on touch devices (5 rows x 40px). Use to keep mobile views compact. */
  isTouchDevice?: boolean;
  /** Apply the hover treatment to body rows. Defaults to `false`. */
  withBodyRowHover?: boolean;
  /** Called with the TanStack `Row` when any body row is clicked. */
  onRowClick?: (row: Row<AppTableFeatures, RevampTableRow>) => void;
  /** Data mode only — pass children via composition mode (`RevampTableCompositionProps`) instead. */
  children?: never;
};

/**
 * Composition mode: `RevampTable` as a styled `<table>` you compose
 * `<thead>`/`<tbody>` into yourself. Replaces the former `RevampTableRoot`.
 */
export type RevampTableCompositionProps = ComponentPropsWithoutRef<'table'> & {
  /** Apply the hover treatment to body rows. Defaults to `true`. */
  withBodyRowHover?: boolean;
  /** Apply the pointer cursor to body rows. Defaults to `withBodyRowHover`. */
  withBodyRowCursor?: boolean;
};

export type PickedIconProps = Pick<IconProps, 'iconName' | 'fillColor'>;

export const statusIconProps: Record<RevampTableKnownStatus, PickedIconProps> =
  {
    Success: {
      iconName: 'CheckmarkCircle',
      fillColor: 'var(--viz-bullish)',
    },
    Failed: {
      iconName: 'CloseCircle',
      fillColor: 'var(--viz-bearish)',
    },
  };
